import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  User, 
  Utensils, 
  Syringe, 
  ArrowRight, 
  Clock, 
  ExternalLink, 
  X, 
  Command, 
  Plus, 
  Lock, 
  Shield, 
  CheckCircle2, 
  AlertTriangle, 
  AlertCircle,
  Sparkles,
  ChevronRight
} from 'lucide-react';
import { patientsAPI, foodAPI, parenteralAPI } from '../services/api';
import { isEnteralItem } from '../utils/routeClassification';

export default function CommandPalette({ open, onOpenChange, user }) {
  const [query, setQuery] = useState('');
  const [patients, setPatients] = useState([]);
  const [foods, setFoods] = useState([]);
  const [parenteral, setParenteral] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const navigate = useNavigate();
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Global Keyboard Listener for Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        onOpenChange(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange]);

  // Load datasets when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);

      // Load data in parallel if not already cached
      if (patients.length === 0 || foods.length === 0 || parenteral.length === 0) {
        setLoading(true);
        Promise.all([
          patientsAPI.getAll().catch(() => []),
          foodAPI.getAll().catch(() => []),
          parenteralAPI.getAll().catch(() => [])
        ]).then(([pts, fds, pnt]) => {
          setPatients(Array.isArray(pts) ? pts : []);
          setFoods(Array.isArray(fds) ? fds : []);
          setParenteral(Array.isArray(pnt) ? pnt : []);
        }).finally(() => {
          setLoading(false);
        });
      }
    }
  }, [open]);

  // Quick App Navigation Actions
  const quickActions = useMemo(() => {
    const actions = [
      {
        id: 'nav-patients',
        type: 'action',
        title: 'Daftar Manajemen Pasien',
        description: 'Buka direktori data pasien & status rawat inap',
        icon: User,
        action: () => navigate('/patients'),
        category: 'Aksi & Navigasi'
      },
      {
        id: 'nav-foods',
        type: 'action',
        title: 'Database Makanan Oral & Enteral',
        description: 'Kelola data gizi bahan makanan dan susu formula',
        icon: Utensils,
        action: () => navigate('/foods'),
        category: 'Aksi & Navigasi'
      },
      {
        id: 'nav-parenteral',
        type: 'action',
        title: 'Database Nutrisi Parenteral',
        description: 'Daftar produk cairan infus, asam amino, lipid & elektrolit',
        icon: Syringe,
        action: () => navigate('/parenteral'),
        category: 'Aksi & Navigasi'
      },
      {
        id: 'nav-change-password',
        type: 'action',
        title: 'Ubah Password Akun',
        description: 'Perbarui kata sandi login keamanan akun',
        icon: Lock,
        action: () => navigate('/change-password'),
        category: 'Aksi & Navigasi'
      }
    ];

    if (user?.role === 'admin') {
      actions.unshift({
        id: 'nav-admin',
        type: 'action',
        title: 'Portal Admin Dashboard',
        description: 'Manajemen pengguna, audit log, & konfigurasi sistem',
        icon: Shield,
        action: () => navigate('/admin'),
        category: 'Aksi & Navigasi'
      });
    }

    return actions;
  }, [user, navigate]);

  // Filtered Results categorized
  const { filteredPatients, filteredFoods, filteredParenteral, filteredActions, flatList } = useMemo(() => {
    const q = query.trim().toLowerCase();

    // Filter Actions
    const actions = q
      ? quickActions.filter(a => 
          a.title.toLowerCase().includes(q) || 
          a.description.toLowerCase().includes(q)
        )
      : quickActions;

    // Filter Patients
    const pts = q
      ? patients.filter(p => 
          (p.name && p.name.toLowerCase().includes(q)) ||
          (p.mr_number && p.mr_number.toLowerCase().includes(q)) ||
          (p.diagnosis && p.diagnosis.toLowerCase().includes(q))
        ).slice(0, 8)
      : patients.slice(0, 5);

    // Filter Foods
    const fds = q
      ? foods.filter(f => 
          f.name && f.name.toLowerCase().includes(q)
        ).slice(0, 6)
      : foods.slice(0, 3);

    // Filter Parenteral
    const pnt = q
      ? parenteral.filter(p => 
          p.name && p.name.toLowerCase().includes(q)
        ).slice(0, 6)
      : parenteral.slice(0, 3);

    // Build unified flat list for keyboard arrow navigation
    const flat = [];

    pts.forEach(p => {
      flat.push({
        id: `patient-${p.id}`,
        type: 'patient',
        data: p,
        onSelect: () => navigate(`/patients/${p.id}/calculate`)
      });
    });

    fds.forEach(f => {
      flat.push({
        id: `food-${f.id || f._id || f.name}`,
        type: 'food',
        data: f,
        onSelect: () => navigate('/foods')
      });
    });

    pnt.forEach(p => {
      flat.push({
        id: `parenteral-${p.id || p._id || p.name}`,
        type: 'parenteral',
        data: p,
        onSelect: () => navigate('/parenteral')
      });
    });

    actions.forEach(a => {
      flat.push({
        id: a.id,
        type: 'action',
        data: a,
        onSelect: a.action
      });
    });

    return {
      filteredPatients: pts,
      filteredFoods: fds,
      filteredParenteral: pnt,
      filteredActions: actions,
      flatList: flat
    };
  }, [query, patients, foods, parenteral, quickActions, navigate]);

  // Keep selected index within bounds
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleSelect = (item) => {
    if (!item) return;
    onOpenChange(false);
    item.onSelect();
  };

  const handleKeyDown = (e) => {
    if (flatList.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % flatList.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + flatList.length) % flatList.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flatList[selectedIndex]) {
        handleSelect(flatList[selectedIndex]);
      }
    }
  };

  // Scroll active item into view
  useEffect(() => {
    if (listRef.current) {
      const activeEl = listRef.current.querySelector('[data-active="true"]');
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-12 sm:pt-20 px-3 sm:px-4">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity animate-in fade-in duration-150"
        onClick={() => onOpenChange(false)}
      />

      {/* Spotlight Window */}
      <div 
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[82vh] z-10 animate-in zoom-in-95 duration-150"
        onKeyDown={handleKeyDown}
      >
        {/* Search Header Bar */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-100 bg-slate-50/50">
          <Search className="h-5 w-5 text-emerald-600 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cari pasien, makanan oral, formula enteral, parenteral, atau menu..."
            className="flex-1 text-sm bg-transparent border-none outline-none text-slate-900 placeholder:text-slate-400"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200/60"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <kbd className="hidden sm:inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-medium text-slate-400 bg-slate-200/70 rounded-md border border-slate-300">
            ESC
          </kbd>
        </div>

        {/* Results List */}
        <div 
          ref={listRef}
          className="overflow-y-auto p-2 space-y-4 flex-1 divide-y divide-slate-100"
        >
          {loading ? (
            <div className="py-12 text-center text-slate-400 text-xs flex flex-col items-center gap-2">
              <div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
              <span>Memuat indeks pencarian klinis...</span>
            </div>
          ) : flatList.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-xs">
              Tidak ada data yang cocok dengan kata kunci "{query}"
            </div>
          ) : (
            <>
              {/* Category: Pasien */}
              {filteredPatients.length > 0 && (
                <div className="space-y-1 pt-1">
                  <p className="px-3 py-1 text-[11px] font-bold tracking-wider uppercase text-slate-400 flex items-center gap-1.5">
                    <User className="h-3 w-3 text-emerald-600" /> Pasien ({filteredPatients.length})
                  </p>
                  {filteredPatients.map((p) => {
                    const flatIdx = flatList.findIndex(item => item.id === `patient-${p.id}`);
                    const isActive = flatIdx === selectedIndex;
                    const pct = p.latest_intake && p.latest_target ? Math.round((p.latest_intake / p.latest_target) * 100) : null;

                    return (
                      <div
                        key={p.id}
                        data-active={isActive}
                        onClick={() => handleSelect(flatList[flatIdx])}
                        onMouseEnter={() => setSelectedIndex(flatIdx)}
                        className={`flex items-center justify-between p-2.5 rounded-xl cursor-pointer transition-colors ${
                          isActive 
                            ? 'bg-emerald-50 text-emerald-950 ring-1 ring-emerald-500/30' 
                            : 'hover:bg-slate-50 text-slate-800'
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                            isActive ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-800'
                          }`}>
                            {p.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-xs sm:text-sm truncate">{p.name}</span>
                              {p.mr_number && (
                                <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.2 rounded font-mono">
                                  RM: {p.mr_number}
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                              <span>Usia: {p.age || '-'} thn</span>
                              <span>•</span>
                              <span>{p.gender === 'male' ? 'Laki-laki' : p.gender === 'female' ? 'Perempuan' : '-'}</span>
                              {pct !== null && (
                                <>
                                  <span>•</span>
                                  <span className={`font-semibold ${pct >= 80 && pct <= 120 ? 'text-emerald-700' : 'text-amber-700'}`}>
                                    Intake: {pct}% target
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          <span className="text-[11px] font-semibold text-emerald-700 hidden sm:inline">Recall</span>
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Category: Makanan & Enteral */}
              {filteredFoods.length > 0 && (
                <div className="space-y-1 pt-2">
                  <p className="px-3 py-1 text-[11px] font-bold tracking-wider uppercase text-slate-400 flex items-center gap-1.5">
                    <Utensils className="h-3 w-3 text-emerald-600" /> Makanan & Formula Enteral ({filteredFoods.length})
                  </p>
                  {filteredFoods.map((f, idx) => {
                    const flatIdx = flatList.findIndex(item => item.id === `food-${f.id || f._id || f.name}`);
                    const isActive = flatIdx === selectedIndex;
                    const isEnteral = isEnteralItem(f);

                    return (
                      <div
                        key={f.id || f._id || idx}
                        data-active={isActive}
                        onClick={() => handleSelect(flatList[flatIdx])}
                        onMouseEnter={() => setSelectedIndex(flatIdx)}
                        className={`flex items-center justify-between p-2.5 rounded-xl cursor-pointer transition-colors ${
                          isActive 
                            ? 'bg-emerald-50 text-emerald-950 ring-1 ring-emerald-500/30' 
                            : 'hover:bg-slate-50 text-slate-800'
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm flex-shrink-0 ${
                            isEnteral ? 'bg-sky-100 text-sky-800' : 'bg-emerald-100 text-emerald-800'
                          }`}>
                            {isEnteral ? '🥣' : '🥗'}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-xs sm:text-sm truncate">{f.name}</span>
                              <span className={`text-[9.5px] font-bold px-1.5 py-0.2 rounded ${
                                isEnteral ? 'bg-sky-100 text-sky-800' : 'bg-emerald-100 text-emerald-800'
                              }`}>
                                {isEnteral ? 'Enteral' : 'Oral'}
                              </span>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {Math.round(f.macronutrients?.energy_kcal || 0)} Kcal / {f.unit} • P: {f.macronutrients?.protein_g || 0}g • KH: {f.macronutrients?.carbohydrate_g || 0}g
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Category: Nutrisi Parenteral */}
              {filteredParenteral.length > 0 && (
                <div className="space-y-1 pt-2">
                  <p className="px-3 py-1 text-[11px] font-bold tracking-wider uppercase text-slate-400 flex items-center gap-1.5">
                    <Syringe className="h-3 w-3 text-purple-600" /> Nutrisi Parenteral & Infus ({filteredParenteral.length})
                  </p>
                  {filteredParenteral.map((p, idx) => {
                    const flatIdx = flatList.findIndex(item => item.id === `parenteral-${p.id || p._id || p.name}`);
                    const isActive = flatIdx === selectedIndex;

                    return (
                      <div
                        key={p.id || p._id || idx}
                        data-active={isActive}
                        onClick={() => handleSelect(flatList[flatIdx])}
                        onMouseEnter={() => setSelectedIndex(flatIdx)}
                        className={`flex items-center justify-between p-2.5 rounded-xl cursor-pointer transition-colors ${
                          isActive 
                            ? 'bg-purple-50 text-purple-950 ring-1 ring-purple-500/30' 
                            : 'hover:bg-slate-50 text-slate-800'
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-800 flex items-center justify-center text-sm flex-shrink-0">
                            💉
                          </div>
                          <div className="min-w-0 flex-1">
                            <span className="font-semibold text-xs sm:text-sm truncate block">{p.name}</span>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {Math.round(p.macronutrients?.energy_kcal || 0)} Kcal / {p.unit} • P: {p.macronutrients?.protein_g || 0}g
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Category: Aksi & Navigasi Cepat */}
              {filteredActions.length > 0 && (
                <div className="space-y-1 pt-2">
                  <p className="px-3 py-1 text-[11px] font-bold tracking-wider uppercase text-slate-400 flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3 text-amber-500" /> Aksi & Navigasi Cepat
                  </p>
                  {filteredActions.map((a) => {
                    const flatIdx = flatList.findIndex(item => item.id === a.id);
                    const isActive = flatIdx === selectedIndex;
                    const Icon = a.icon;

                    return (
                      <div
                        key={a.id}
                        data-active={isActive}
                        onClick={() => handleSelect(flatList[flatIdx])}
                        onMouseEnter={() => setSelectedIndex(flatIdx)}
                        className={`flex items-center justify-between p-2.5 rounded-xl cursor-pointer transition-colors ${
                          isActive 
                            ? 'bg-emerald-50 text-emerald-950 ring-1 ring-emerald-500/30' 
                            : 'hover:bg-slate-50 text-slate-800'
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isActive ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'
                          }`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <span className="font-semibold text-xs sm:text-sm block">{a.title}</span>
                            <span className="text-[11px] text-slate-400 block truncate">{a.description}</span>
                          </div>
                        </div>
                        <ArrowRight className="h-4 w-4 text-slate-400" />
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Spotlight Footer: Keyboard Shortcuts Guide */}
        <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white border rounded text-[10px] text-slate-600 font-mono">↑↓</kbd> Navigasi
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white border rounded text-[10px] text-slate-600 font-mono">↵</kbd> Pilih / Buka
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white border rounded text-[10px] text-slate-600 font-mono">ESC</kbd> Tutup
            </span>
          </div>
          <span className="font-semibold text-emerald-700">NUTRIMUS Spotlight</span>
        </div>
      </div>
    </div>
  );
}
